import { ReactNode, useCallback, useLayoutEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { StyledCanvas } from './dynamic-dots-styles';
import { DOT_COLOUR, DOT_RADIUS, DOT_SEPARATION, MIN_PADDING } from './dynamic-dots.constants';

// This component is used to create a grid of dots that dynamically resizes with the container
// It is a canvas as using an image would result in stretching or points too close to the edge
export const DynamicDots = ({
  children
}: {
  children: ReactNode;
}) => {
  const dotsContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dimensionsRef = useRef({
    width: 0,
    height: 0
  });
  const drawDots = useCallback((containerHeight: number, containerWidth: number) => {
    const canvasContext = canvasRef.current?.getContext('2d');
    if (!canvasContext) return;
    canvasContext.clearRect(0, 0, containerWidth, containerHeight);
    let xPadding = containerWidth % DOT_SEPARATION / 2;
    let yPadding = containerHeight % DOT_SEPARATION / 2;
    if (xPadding < MIN_PADDING) xPadding += MIN_PADDING;
    if (yPadding < MIN_PADDING) yPadding += MIN_PADDING;
    for (let x = xPadding; x < containerWidth - xPadding / 2; x += DOT_SEPARATION) {
      for (let y = yPadding; y < containerHeight - yPadding / 2; y += DOT_SEPARATION) {
        canvasContext.beginPath();
        canvasContext.fillStyle = DOT_COLOUR;
        canvasContext.arc(x, y, DOT_RADIUS, 0, 2 * Math.PI);
        canvasContext.fill();
        canvasContext.closePath();
      }
    }
  }, []);
  const updateCanvasSize = useDebouncedCallback(() => {
    if (!dotsContainerRef.current || !canvasRef.current) return;
    const {
      width,
      height
    } = dotsContainerRef.current.getBoundingClientRect();
    const widthDifference = Math.abs(width - dimensionsRef.current.width);
    if (widthDifference > 80 || height !== dimensionsRef.current.height) {
      dimensionsRef.current = {
        width,
        height
      };
      canvasRef.current.width = width;
      canvasRef.current.height = height;
      drawDots(height, width);
    }
  }, 200);
  useLayoutEffect(() => {
    updateCanvasSize();
    const resizeObserver = new ResizeObserver(updateCanvasSize);
    if (dotsContainerRef.current) {
      resizeObserver.observe(dotsContainerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [updateCanvasSize]);
  return <div ref={dotsContainerRef} data-sentry-component="DynamicDots" data-sentry-source-file="dynamic-dots.tsx">
      <StyledCanvas ref={canvasRef} data-sentry-element="StyledCanvas" data-sentry-source-file="dynamic-dots.tsx" />
      {children}
    </div>;
};